"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { AppDispatch } from "../../../../../../app/store";
import { RootState } from "../../../../../../app/rootReducer";
import { fetchCompanyProfileAction } from "../../slices/companyProfileSlice";
import {
  companyProfileFormSchema,
  CompanyProfileFormData,
} from "../../schemas/companyProfileSchema";
import { updateMyCompanyProfile } from "@/api/updateMyCompanyProfile";
import { useNotification } from "@/context/NotificationContext";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

interface ReadOnlyFieldProps {
  label: string;
  value?: string | null;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
  <div>
    <p className="text-sm font-normal text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="mt-1 text-sm text-gray-900 dark:text-white">
      {value && value.trim() !== "" ? value : "—"}
    </p>
  </div>
);

const EMPTY_FORM: CompanyProfileFormData = {
  addressLine1: "",
  city: "",
  state: "",
  zipCode: "",
  isMailingAddressSame: true,
  mailingAddressLine1: "",
  mailingCity: "",
  mailingState: "",
  mailingZipCode: "",
};

// Empty strings back to null so cleared fields clear on Salesforce too.
const nullIfEmpty = (value: string): string | null =>
  value.trim() === "" ? null : value;

const CompanyProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showNotification } = useNotification();
  const [isSaving, setIsSaving] = useState(false);

  const { companyProfile } = useSelector(
    (state: RootState) => state.companyProfile,
  );

  const form = useForm<CompanyProfileFormData>({
    resolver: zodResolver(companyProfileFormSchema),
    defaultValues: EMPTY_FORM,
  });
  const { control, handleSubmit, reset, formState } = form;

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
  }, [dispatch]);

  // Hydrate the form once the profile loads (and after a successful save).
  useEffect(() => {
    if (!companyProfile) {
      return;
    }
    reset({
      addressLine1: companyProfile.addressLine1 ?? "",
      city: companyProfile.city ?? "",
      state: companyProfile.state ?? "",
      zipCode: companyProfile.zipCode ?? "",
      isMailingAddressSame: companyProfile.isMailingAddressSame ?? true,
      mailingAddressLine1: companyProfile.mailingAddressLine1 ?? "",
      mailingCity: companyProfile.mailingCity ?? "",
      mailingState: companyProfile.mailingState ?? "",
      mailingZipCode: companyProfile.mailingZipCode ?? "",
    });
  }, [companyProfile, reset]);

  const isMailingSame = useWatch({ control, name: "isMailingAddressSame" });

  const onSubmit = (values: CompanyProfileFormData) => {
    setIsSaving(true);
    updateMyCompanyProfile({
      addressLine1: nullIfEmpty(values.addressLine1),
      city: nullIfEmpty(values.city),
      state: nullIfEmpty(values.state),
      zipCode: nullIfEmpty(values.zipCode),
      isMailingAddressSame: values.isMailingAddressSame,
      mailingAddressLine1: nullIfEmpty(values.mailingAddressLine1),
      mailingCity: nullIfEmpty(values.mailingCity),
      mailingState: nullIfEmpty(values.mailingState),
      mailingZipCode: nullIfEmpty(values.mailingZipCode),
    })
      .then(() => {
        showNotification(
          "Company address updated",
          "Your changes have been saved.",
          "success",
        );
        dispatch(fetchCompanyProfileAction());
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Profile</CardTitle>
        <CardDescription>
          Company name, email, and website are managed in Salesforce. You can
          update your address below — changes sync to Salesforce and connected
          apps.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <ReadOnlyField
            label="Company Name"
            value={companyProfile?.companyName}
          />
          <ReadOnlyField
            label="Company Email"
            value={companyProfile?.companyEmail}
          />
          <ReadOnlyField
            label="Website URL"
            value={companyProfile?.websiteUrl}
          />

          <Form {...form}>
            <form
              className="space-y-6 pt-2 border-t dark:border-slate-700"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormField
                control={control}
                name="addressLine1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State / Province</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ZIP / Postal Code</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="isMailingAddressSame"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                    <div className="space-y-0.5">
                      <FormLabel>Mailing address same as above</FormLabel>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Turn off to enter a separate mailing address.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {!isMailingSame && (
                <div className="space-y-6">
                  <FormField
                    control={control}
                    name="mailingAddressLine1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mailing Street Address</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <FormField
                      control={control}
                      name="mailingCity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mailing City</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="mailingState"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mailing State / Province</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={control}
                    name="mailingZipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mailing ZIP / Postal Code</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <div className="flex justify-end">
                <Button disabled={isSaving || !formState.isDirty} type="submit">
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyProfile;
